import {DraggableAttributes, useDraggable, DraggableSyntheticListeners} from '@dnd-kit/core'
import {CSS} from '@dnd-kit/utilities'
import {StationLayout} from '@domain/kiln-monitoring'
import {Box, Stack, Typography} from '@mui/material'
import {StationPart} from '@widgets/kms'

import {getKilnPartDimensions, getKilnShellDimensions} from '../../helpers'
import {useKilnConfig} from '../../providers'

import {KilnPartDimensionsScale} from './common'
import {DragHandle} from './DragHandle'

type StationProps = {
  station: StationLayout
  positionYOffset: number
  isCurrentlySelected: boolean
  onClick: () => void
}

export const Station: React.FC<StationProps> = ({
  station,
  positionYOffset,
  isCurrentlySelected,
  onClick
}) => {
  const {kilnConfiguration, kilnLayout} = useKilnConfig()

  const {metersInPixel} = getKilnShellDimensions(kilnConfiguration, kilnLayout)
  const {
    width: {widthInMeters}
  } = getKilnPartDimensions(station)

  const {attributes, listeners, setNodeRef, transform, isDragging} = useDraggable({
    id: station.id
  })

  const style = {
    transform: CSS.Translate.toString(transform)
  }

  const xPosition =
    widthInMeters !== undefined ? station.x + (widthInMeters / 2) * metersInPixel : station.x

  return (
    <>
      <Box
        ref={setNodeRef}
        style={style}
        sx={{
          position: 'absolute',
          top: station.y + positionYOffset,
          left: xPosition,
          width: station.width,
          height: station.height
        }}
      >
        <StationPart
          id={station.id}
          x={0}
          y={0}
          thrustRollerPosition={station.thrustRollerPosition}
          height={station.height}
          width={station.width}
        />
      </Box>
      <StationOverlay
        isCurrentlySelected={isCurrentlySelected}
        onClick={onClick}
        stationLayout={station}
        style={style}
        attributes={attributes}
        listeners={listeners}
        isDragging={isDragging}
      />
    </>
  )
}

const StationOverlay = ({
  stationLayout,
  style,
  isCurrentlySelected,
  onClick,
  attributes,
  listeners,
  isDragging
}: {
  stationLayout: StationLayout
  style: {
    transform: string | undefined
  }
  isCurrentlySelected: boolean
  onClick: () => void
  attributes: DraggableAttributes
  listeners: DraggableSyntheticListeners
  isDragging: boolean
}) => {
  const {kilnLayout} = useKilnConfig()

  const {
    width: {isWidthEmpty}
  } = getKilnPartDimensions(stationLayout)

  return (
    <Box
      style={style}
      sx={{
        position: 'absolute',
        border: '2px solid rgba(41, 51, 61, 0.2)',
        borderRadius: '8px',
        width: kilnLayout.stationOverlayWidth,
        height: kilnLayout.interactiveOverlayHeight,
        top: 0,
        left: stationLayout.x - (stationLayout.thrustRollerPosition === 'none' ? 57 : 37),
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        cursor: 'pointer',
        p: '8px',
        boxShadow: isDragging ? '0px 2px 32px 0px #00000014;' : 'none',
        ...(isCurrentlySelected
          ? {border: '2px solid #016AD4', background: 'rgba(230, 240, 251, 0.4)'}
          : {})
      }}
      data-test-id="station-overlay"
      onClick={onClick}
    >
      <Stack direction="row" alignItems="center" spacing={3}>
        <DragHandle isDisabled={isWidthEmpty} attributes={attributes} listeners={listeners} />
        <Typography noWrap>{stationLayout.name ?? ''}</Typography>
      </Stack>
      <KilnPartDimensionsScale selectedKilnPart={stationLayout} />
    </Box>
  )
}
